"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import {
  CheckCircle2,
  Lightbulb,
  Compass,
  Palette,
  Code2,
  Rocket,
} from "lucide-react";
import { Section } from "@/components/ui/Section";
import { ProcessSlide, Step } from "@/components/ui/ProcessSlide";
import { ProcessNavigation } from "@/components/ui/ProcessNavigation";

const STEPS: readonly Step[] = [
  { id: "discovery", icon: Lightbulb, number: "01" },
  { id: "strategy", icon: Compass, number: "02" },
  { id: "design", icon: Palette, number: "03" },
  { id: "development", icon: Code2, number: "04" },
  { id: "launch", icon: Rocket, number: "05" },
] as const;

export function Process() {
  const t = useTranslations("Process");
  const tSteps = useTranslations("Process.steps");
  const containerRef = useRef<HTMLDivElement>(null);

  // Slide state: [index, direction, isFirstView]
  // isFirstView = true the very first time a slide is displayed.
  // Stored in state (not read from ref.current during render) to satisfy React rules.
  const [[slideIndex, direction, isFirstView], setSlideState] = useState<
    [number, number, boolean]
  >([0, 0, true]);

  // Mutable Set used ONLY inside event-handlers/effects — never read during render.
  const seenSlides = useRef<Set<number>>(new Set([0]));

  const numSlides = STEPS.length;
  const trackHeight = `${(numSlides + 1) * 100}vh`;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const segment = Math.min(numSlides - 1, Math.floor(latest * numSlides));
    if (segment !== slideIndex) {
      const firstView = !seenSlides.current.has(segment);
      seenSlides.current.add(segment);
      setSlideState([segment, segment > slideIndex ? 1 : -1, firstView]);
    }
  });

  const activeTimelineIndex = slideIndex;

  const handleNavClick = (index: number) => {
    if (!containerRef.current) return;

    const containerTop =
      containerRef.current.getBoundingClientRect().top + window.scrollY;
    const containerHeight = containerRef.current.offsetHeight;
    const scrollableHeight = containerHeight - window.innerHeight;
    const progress = (index + 0.5) / numSlides;
    const targetY = containerTop + scrollableHeight * progress;

    window.scrollTo({
      top: targetY,
      behavior: "smooth",
    });
  };

  return (
    <Section
      id="process"
      noContainer
      noPadding
      title={t("title")}
      subtitle={t("subtitle")}
      className="relative bg-(--bg-primary) py-24"
    >
      {/* Description — visible on desktop above the scroll track */}
      <p className="hidden lg:block text-center text-(--text-secondary) text-base leading-relaxed max-w-4xl mx-auto px-6 mb-6 relative z-10">
        {t("description")}
      </p>
      {/* Desktop Parallax Storytelling Flow */}
      <div
        ref={containerRef}
        style={{ height: trackHeight }}
        className="relative hidden lg:block"
      >
        <div className="sticky top-20 h-[calc(100vh-5rem)] w-full flex flex-col">
          <ProcessNavigation
            steps={STEPS}
            activeIndex={activeTimelineIndex}
            onStepClick={handleNavClick}
          />

          <div className="relative flex-1 min-h-0">
            <AnimatePresence custom={direction}>
              <ProcessSlide
                key={`step-${slideIndex}`}
                step={STEPS[slideIndex]}
                direction={direction}
                isFirstView={isFirstView}
              />
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Mobile / Tablet Layout (Standard vertical scroll) */}
      <div className="lg:hidden w-full px-6 py-24 flex flex-col gap-24">
        <div className="text-center">
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-(--brand-primary) mb-4 block">
            {t("title")}
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-(--text-primary) leading-[1.1] mb-6">
            {t("subtitle")}
          </h2>
          <p className="text-base text-(--text-secondary) leading-relaxed">
            {t("description")}
          </p>
        </div>

        <div className="flex flex-col gap-16 relative">
          <div className="absolute left-6 top-10 bottom-10 w-0.5 bg-(--border-subtle) z-0" />

          {STEPS.map((step) => {
            const detailsRaw = tSteps.raw(`${step.id}.details`);
            const details = Array.isArray(detailsRaw) ? detailsRaw : [];

            return (
              <div key={step.id} className="relative z-10">
                <div className="flex flex-col gap-8 bg-(--bg-card) border border-(--border-subtle) rounded-3xl p-6 sm:p-8 shadow-xl">
                  <div className="flex items-center gap-4 border-b border-(--border-subtle) pb-6">
                    <div className="w-12 h-12 rounded-full bg-(--brand-primary) flex items-center justify-center shrink-0 shadow-[0_0_20px_var(--brand-glow)]">
                      <step.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-0.5 bg-(--brand-primary) rounded-full inline-block shrink-0" />
                        <span className="text-xs font-bold text-(--text-tertiary) tracking-widest uppercase">
                          {t("phase")} {step.number}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-(--text-primary)">
                        {tSteps(`${step.id}.title`)}
                      </h3>
                    </div>
                  </div>

                  <p className="text-(--text-secondary) text-sm leading-relaxed">
                    {tSteps(`${step.id}.description`)}
                  </p>

                  {details.length > 0 && (
                    <div className="grid grid-cols-1 gap-3 mt-2">
                      {details.map((d, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 bg-(--bg-primary)/50 rounded-lg p-3 border border-(--border-subtle)"
                        >
                          <CheckCircle2 className="w-4 h-4 text-(--brand-primary) shrink-0 mt-0.5" />
                          <span className="text-xs font-medium text-(--text-secondary)">
                            {d}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
